/**
 * Cognito over plain fetch.
 *
 * The Amplify/Cognito SDKs are a lot of bytes for three endpoints, and this app
 * is meant to boot fast on a phone with one bar. USER_PASSWORD_AUTH sends the
 * password to Cognito over TLS, which is what the SRP dance ultimately protects
 * against on a hostile network — and there is no hostile network between the
 * browser and cognito-idp.
 */

import { config } from './config';
import { hostedSignOutUrl, oauthConfigured, refreshOAuth } from './oauth';

const ENDPOINT = `https://cognito-idp.${config.region}.amazonaws.com/`;
const STORAGE_KEY = 'edh.auth';

export interface Tokens {
  accessToken: string;
  refreshToken: string;
  /** Epoch ms. Refreshed a minute early so a request never races the clock. */
  expiresAt: number;
  username: string;
  /**
   * Which flow minted these. A hosted-UI session must refresh through the
   * /oauth2/token endpoint; InitiateAuth will not accept its refresh token.
   */
  source: 'password' | 'google';
}

export class AuthError extends Error {}
export class NewPasswordRequired extends Error {
  constructor(readonly session: string, readonly username: string) {
    super('new password required');
  }
}

async function call<T>(target: string, payload: unknown): Promise<T> {
  const res = await fetch(ENDPOINT, {
    method: 'POST',
    headers: {
      'content-type': 'application/x-amz-json-1.1',
      'x-amz-target': `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(payload),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, any>;
  if (!res.ok) {
    throw new AuthError(data.message ?? data.__type ?? `auth failed (${res.status})`);
  }
  return data as T;
}

interface AuthResponse {
  AuthenticationResult?: {
    AccessToken: string;
    RefreshToken?: string;
    ExpiresIn: number;
  };
  ChallengeName?: string;
  Session?: string;
}

function persist(result: NonNullable<AuthResponse['AuthenticationResult']>, username: string, existingRefresh?: string): Tokens {
  return store({
    accessToken: result.AccessToken,
    refreshToken: result.RefreshToken ?? existingRefresh ?? '',
    expiresAt: Date.now() + (result.ExpiresIn - 60) * 1000,
    username,
    source: 'password',
  });
}

export function store(tokens: Tokens): Tokens {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
  return tokens;
}

export function stored(): Tokens | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Tokens) : null;
  } catch {
    return null;
  }
}

export function signOut(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Our own Cognito subject, read straight out of the access token.
 *
 * Deliberately not verified here — the signature is checked by the API on every
 * request, and a client cannot gain anything by lying to itself. This is only
 * used to answer "which guild member am I?" before the answer arrives.
 */
export function subject(): string | null {
  const token = stored()?.accessToken;
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return (JSON.parse(decoded) as { sub?: string }).sub ?? null;
  } catch {
    return null;
  }
}

/**
 * End the session properly and return to the sign-in screen.
 *
 * Clearing the local tokens is not enough for a Google session: Cognito keeps
 * its own session cookie, so "Continue with Google" would silently sign the
 * same person straight back in without ever showing an account picker. On a
 * phone being handed round a table that is the wrong behaviour, so the hosted
 * logout endpoint gets called too.
 *
 * The cached league is deliberately left alone. It is not a secret from the
 * group, and keeping it means the next sign-in is instant and works offline.
 */
export function endSession(): void {
  const current = stored();
  signOut();
  if (current?.source === 'google' && oauthConfigured()) {
    window.location.assign(hostedSignOutUrl());
    return;
  }
  window.location.assign(window.location.origin);
}

export async function signIn(username: string, password: string): Promise<Tokens> {
  const data = await call<AuthResponse>('InitiateAuth', {
    AuthFlow: 'USER_PASSWORD_AUTH',
    ClientId: config.userPoolClientId,
    AuthParameters: { USERNAME: username, PASSWORD: password },
  });

  // Admin-created accounts land here on first sign-in; nobody self-signs up.
  if (data.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
    throw new NewPasswordRequired(data.Session!, username);
  }
  if (!data.AuthenticationResult) throw new AuthError('unexpected auth response');
  return persist(data.AuthenticationResult, username);
}

export async function completeNewPassword(
  session: string,
  username: string,
  newPassword: string,
): Promise<Tokens> {
  const data = await call<AuthResponse>('RespondToAuthChallenge', {
    ChallengeName: 'NEW_PASSWORD_REQUIRED',
    ClientId: config.userPoolClientId,
    Session: session,
    ChallengeResponses: { USERNAME: username, NEW_PASSWORD: newPassword },
  });
  if (!data.AuthenticationResult) throw new AuthError('could not set the new password');
  return persist(data.AuthenticationResult, username);
}

let refreshing: Promise<Tokens> | null = null;

/**
 * Returns a valid access token, refreshing if needed. Offline, this throws — the
 * caller treats that the same as any other network failure and works from cache.
 */
export async function accessToken(): Promise<string> {
  const current = stored();
  if (!current) throw new AuthError('signed out');
  if (current.expiresAt > Date.now()) return current.accessToken;
  if (!current.refreshToken) throw new AuthError('session expired');

  // Several screens can wake at once after a long sleep; only refresh once.
  refreshing ??= (async () => {
    try {
      if (current.source === 'google') {
        const next = await refreshOAuth(current.refreshToken);
        return store({
          ...current,
          accessToken: next.accessToken,
          // A refresh response omits the refresh token; keep the one we have.
          refreshToken: next.refreshToken || current.refreshToken,
          expiresAt: next.expiresAt,
        });
      }
      const data = await call<AuthResponse>('InitiateAuth', {
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: config.userPoolClientId,
        AuthParameters: { REFRESH_TOKEN: current.refreshToken },
      });
      if (!data.AuthenticationResult) throw new AuthError('refresh failed');
      return persist(data.AuthenticationResult, current.username, current.refreshToken);
    } finally {
      refreshing = null;
    }
  })();

  return (await refreshing).accessToken;
}
